/**
 * Session Migration Utilities (JS/JSX version)
 * Handles migration from flat session structure to nested session + alternative ranges
 *
 * NOTE: This is plain JS (no TypeScript). Kept the same API and behavior.
 */

// import { CleaningWindow, CleaningSession, TimeRange } from '@/app/types/location.types';

/**
 * @typedef {Object} OldCleaningWindow
 * @property {string} id
 * @property {string} name
 * @property {string[]} days
 * @property {'single'|'multiple'} sessionType
 * @property {{ from: string, to: string }[]} sessions
 */

/**
 * Generate a unique ID for sessions
 * @returns {string}
 */
export function generateSessionId() {
  return `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Create a default session
 * @returns {Object} CleaningSession-like object
 */
export function createDefaultSession() {
  return {
    id: generateSessionId(),
    name: undefined,
    primaryRange: { from: '08:00', to: '12:00' },
    alternativeRanges: [],
  };
}

/**
 * Migrate old flat structure to new nested structure
 * @param {OldCleaningWindow} oldWindow
 * @returns {Object} CleaningWindow-like object
 */
export function migrateWindowData(oldWindow) {
  if (oldWindow.sessionType === 'single') {
    // SINGLE MODE: Convert all ranges to 1 session with alternatives
    const [primary, ...alternatives] = oldWindow.sessions || [];

    return {
      ...oldWindow,
      sessions: [
        {
          id: generateSessionId(),
          name: undefined,
          primaryRange: primary || { from: '08:00', to: '12:00' },
          alternativeRanges: alternatives.slice(0, 2), // Max 2 alternatives
        },
      ],
    };
  }

  // MULTIPLE MODE: Each range becomes an individual session (without alternatives)
  return {
    ...oldWindow,
    sessions: (oldWindow.sessions || []).map((range, index) => ({
      id: generateSessionId(),
      name: `Session ${index + 1}`,
      primaryRange: range,
      alternativeRanges: [],
    })),
  };
}

/**
 * Validate session structure
 * @param {Object} session CleaningSession-like object
 * @returns {string|null}
 */
export function validateSession(session) {
  // Must have primary range
  if (!session?.primaryRange?.from || !session?.primaryRange?.to) {
    return 'Primary range is required';
  }

  const alternativeRanges = session.alternativeRanges || [];

  // Max 2 alternative ranges
  if (alternativeRanges.length > 2) {
    return 'Maximum 2 alternative ranges allowed';
  }

  // Validate all ranges have valid times
  const allRanges = [session.primaryRange, ...alternativeRanges];

  for (const range of allRanges) {
    if (!range?.from || !range?.to) {
      return 'All time ranges must have from and to values';
    }

    // Validate from < to
    const fromMinutes = timeToMinutes(range.from);
    const toMinutes = timeToMinutes(range.to);

    if (fromMinutes >= toMinutes) {
      return 'End time must be after start time';
    }
  }

  return null;
}

/**
 * Validate window structure
 * @param {Object} window CleaningWindow-like object
 * @returns {string|null}
 */
export function validateWindow(window) {
  const days = window?.days || [];
  const sessions = window?.sessions || [];

  // Must have at least one day
  if (days.length === 0) {
    return 'At least one day must be selected';
  }

  // Must have at least one session
  if (sessions.length === 0) {
    return 'At least one session is required';
  }

  // Single mode: only 1 session
  if (window.sessionType === 'single' && sessions.length !== 1) {
    return 'Single session mode allows only 1 session';
  }

  // Multiple mode: 1-5 sessions (kept message consistent with your comment)
  if (window.sessionType === 'multiple' && (sessions.length < 1 || sessions.length > 5)) {
    return 'Multiple sessions mode allows 1-5 sessions';
  }

  // Validate each session
  for (const session of sessions) {
    const sessionError = validateSession(session);
    if (sessionError) return sessionError;
  }

  // Multiple mode: check for session overlaps
  if (window.sessionType === 'multiple' && sessions.length > 1) {
    const overlapError = validateNoSessionOverlap(window);
    if (overlapError) return overlapError;
  }

  return null;
}

/**
 * Check if two time ranges overlap
 * @param {{from: string, to: string}} a
 * @param {{from: string, to: string}} b
 * @returns {boolean}
 */
export function rangesOverlap(a, b) {
  const aStart = timeToMinutes(a.from);
  const aEnd = timeToMinutes(a.to);
  const bStart = timeToMinutes(b.from);
  const bEnd = timeToMinutes(b.to);

  return aStart < bEnd && aEnd > bStart;
}

/**
 * Validate that sessions don't overlap in multiple mode
 * CRITICAL: Check ALL possible alternative combinations
 * @param {Object} window CleaningWindow-like object
 * @returns {string|null}
 */
export function validateNoSessionOverlap(window) {
  if (window?.sessionType !== 'multiple') return null;

  const sessions = window.sessions || [];

  // Get all possible ranges for each session
  const sessionRanges = sessions.map((session) => ({
    sessionName: session.name || 'Session',
    ranges: [session.primaryRange, ...(session.alternativeRanges || [])],
  }));

  // Compare each pair of sessions
  for (let i = 0; i < sessionRanges.length; i++) {
    for (let j = i + 1; j < sessionRanges.length; j++) {
      const session1 = sessionRanges[i];
      const session2 = sessionRanges[j];

      // Check if ANY combination of ranges overlap
      // This is critical because janitor can choose any alternative
      const canOverlap = session1.ranges.some((r1) =>
        session2.ranges.some((r2) => rangesOverlap(r1, r2)),
      );

      if (canOverlap) {
        return `${session1.sessionName} and ${session2.sessionName} have overlapping time ranges`;
      }
    }
  }

  return null;
}

/**
 * Convert time string to minutes (for comparison)
 * @param {string} time "HH:MM"
 * @returns {number}
 */
export function timeToMinutes(time) {
  const [hours, minutes] = String(time).split(':').map(Number);
  return hours * 60 + minutes;
}

/**
 * Calculate frequency for a window
 * @param {Object} window CleaningWindow-like object
 * @returns {number}
 */
export function calculateWindowFrequency(window) {
  const daysCount = (window?.days || []).length;

  if (window?.sessionType === 'single') {
    // Only 1 session (with possible alternatives)
    return daysCount * 1;
  }

  // Multiple sessions (each one with possible alternatives)
  return daysCount * (window?.sessions || []).length;
}

/**
 * Calculate total frequency across all windows
 * @param {Object[]} windows CleaningWindow-like objects
 * @returns {number}
 */
export function calculateTotalFrequency(windows) {
  return (windows || []).reduce((total, window) => total + calculateWindowFrequency(window), 0);
}
